<template>
  <div class="border rounded p-1 mt-1">
    <div v-if="counts">
      <small class="text-muted">
        В домене: {{ counts.total }} из {{ counts.maxPerDomain }} инструментов.
        В списке модели: {{ counts.listed }} из {{ counts.maxListed }}.
      </small>
      <!-- CUB-913 (§11.2, решение 44): подсказка объясняет, ЧЕМ обоснован предел. Раньше «в
           списке» читалось как экономия контекста; замер 20.09 это опроверг — клиент держит в
           запросе только имена и подгружает схемы по запросу, 114 инструментов стоили 454
           токена. Значит предел держит обозримость, и так его автору и объясняем: список, в
           котором выбирает модель, и список, который автор проверяет глазами. -->
      <div>
        <small class="text-muted">
          Предел списка — про обозримость, а не про цену контекста: клиент держит в запросе
          только имена инструментов и подгружает схемы по запросу (замер 20.09: 114 инструментов
          — 454 токена). Короткий список нужен, чтобы модель не путалась между похожими
          инструментами, а вы могли проверить его глазами. Готовый, но не нужный сейчас
          инструмент держите «в запасе» — он остаётся активным, просто не показывается модели.
        </small>
      </div>
    </div>

    <div
      v-if="targetUpsertChain"
      class="text-warning mt-1"
    >
      <small>
        <feather-icon icon="AlertTriangleIcon" /> У цели есть сборка-триггер с шагом апсерта:
        {{ targetUpsertChain }}. Апсерт внутри триггера больше не рвёт транзакцию записи (Н14
        исправлен, `BUG-652`), но сборку стоит проверить перед активацией инструмента (§7).
      </small>
    </div>

    <div v-if="!toolId">
      <small class="text-muted">Пробный вызов — после первого сохранения инструмента.</small>
    </div>
    <div v-else>
      <label class="mb-0 mt-1">Пробный вызов (§8.4): аргументы модели, JSON</label>
      <b-form-textarea
        v-model="argsText"
        rows="2"
        placeholder="{&quot;limit&quot;: 20}"
      />
      <b-button
        size="sm"
        variant="outline-primary"
        class="mt-1"
        :disabled="loading"
        @click="run"
      >
        <feather-icon icon="PlayIcon" /> Пробный вызов
      </b-button>

      <div
        v-if="argsError"
        class="text-danger mt-1"
      >
        {{ argsError }}
      </div>

      <div
        v-if="result"
        class="mt-1"
      >
        <div v-if="result.success">
          <div class="text-success">
            Ответ — ровно то, что увидит модель:
          </div>
          <pre
            class="bg-light p-1"
            style="white-space:pre-wrap;"
          >{{ pretty(result.answer) }}</pre>
        </div>
        <div v-else>
          <div class="text-danger">
            Отказ — ровно то, что увидела бы модель: {{ result.error }}
          </div>
        </div>
        <small class="text-muted">Проба записана (probedat обновлён) — «активировать» теперь принимается при сохранении.</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BFormTextarea } from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsCustomToolProbe',
  components: { BButton, BFormTextarea },
  props: {
    record: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      counts: null,
      argsText: '{}',
      argsError: '',
      loading: false,
      result: null,
      targetUpsertChain: null,
    }
  },
  computed: {
    toolId() {
      return this.record && this.record.id && this.record.id.value
    },
    targetObjectId() {
      return this.record && this.record.targetobjectid && this.record.targetobjectid.value
    },
  },
  watch: {
    // CUB-908 (§7, §8.3): помечаем цель триггером-апсертом при выборе, до сохранения —
    // предупреждение, не отказ (Н14 больше не рвёт транзакцию, BUG-652).
    targetObjectId: {
      immediate: true,
      handler(id) {
        this.loadTargetUpsertChain(id)
      },
    },
  },
  created() {
    this.loadCounts()
  },
  methods: {
    loadTargetUpsertChain(objectid) {
      this.targetUpsertChain = null
      if (!objectid) return
      this.qryPoll('cbstool_target_upsert_chain', { objectid }).then(d => {
        if (d.result) this.targetUpsertChain = d.result.chain || null
      }).catch(error => console.log('cbstool_target_upsert_chain error', error))
    },
    // /api/qry отвечает либо сразу `{result}`/`{error}`, либо `{thread}` — тогда результат
    // приходит только по опросу `method: 'thread'` (тот же приём, что checkUnique выше по файлу
    // у CbsRecordForm — здесь он неизбежен: cbstool_trycall сам исполняет запрос к данным).
    qryPoll(method, param) {
      return useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method, param },
      }).then(response => {
        const d = response.data || {}
        if (d.thread) return this.pollThread(d.thread)
        return d
      })
    },
    pollThread(threadName) {
      return useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'thread', param: { threadname: threadName } },
      }).then(response => {
        const t = (response.data || {}).thread
        if (t && (t.status === 'done' || t.status === 'error')) return t
        return new Promise(resolve => {
          setTimeout(() => resolve(this.pollThread(threadName)), 400)
        })
      })
    },
    loadCounts() {
      this.qryPoll('cbstool_counts', {}).then(d => {
        if (d.result) this.counts = d.result
      }).catch(error => console.log('cbstool_counts error', error))
    },
    pretty(v) {
      return JSON.stringify(v, null, 2)
    },
    run() {
      this.argsError = ''
      let args
      try {
        args = this.argsText.trim() ? JSON.parse(this.argsText) : {}
      } catch (e) {
        this.argsError = `Аргументы — не JSON: ${e.message}`
        return
      }
      this.loading = true
      this.result = null
      this.qryPoll('cbstool_trycall', { id: this.toolId, args }).then(d => {
        this.loading = false
        if (d.result) {
          this.result = d.result
        } else if (d.error) {
          useCubus.toastError(this, d.error)
        }
        this.loadCounts()
      }).catch(error => {
        this.loading = false
        console.log('cbstool_trycall error', error)
      })
    },
  },
}
</script>

<style scoped>
</style>
